import { useState, useEffect } from 'react'
import axios from 'axios'
import moment from 'moment-timezone'
import PaymentForm from '../components/PaymentForm'
import { getSignupType, requiresDetail, formatSlotLabel, signupCtaLabel } from '../utils/signupSheets'

const ET = 'America/New_York'

const linkifyText = (text) => {
  if (!text) return text
  const urlRegex = /(https?:\/\/[^\s]+)/g
  const parts = text.split(urlRegex)
  return parts.map((part, i) =>
    urlRegex.test(part) ? (
      <a key={i} href={part} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-800 underline">{part}</a>
    ) : part
  )
}

const Events = () => {
  const [events, setEvents] = useState([])
  const [filteredEvents, setFilteredEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedEvent, setSelectedEvent] = useState(null)
  const [showRsvpModal, setShowRsvpModal] = useState(false)
  const [numAdults, setNumAdults] = useState(1)
  const [numChildren, setNumChildren] = useState(0)
  const [showPastEvents, setShowPastEvents] = useState(false)
  const [showPaymentForm, setShowPaymentForm] = useState(false)
  const [rsvpComplete, setRsvpComplete] = useState(false)
  const [confirmationNumber, setConfirmationNumber] = useState(null)
  const [attendeeName, setAttendeeName] = useState('')
  const [attendeeEmail, setAttendeeEmail] = useState('')
  const [lightboxImage, setLightboxImage] = useState(null)
  const [detailEvent, setDetailEvent] = useState(null)
  const [showSignupModal, setShowSignupModal] = useState(false)
  const [signupSlots, setSignupSlots] = useState(null)
  const [selectedRole, setSelectedRole] = useState(null)
  const [selectedSlot, setSelectedSlot] = useState(null)
  const [signupName, setSignupName] = useState('')
  const [signupEmail, setSignupEmail] = useState('')
  const [signupDetail, setSignupDetail] = useState('')
  const [signupComplete, setSignupComplete] = useState(false)
  const [signupSubmitting, setSignupSubmitting] = useState(false)
  const [signupEvent, setSignupEvent] = useState(null)

  // Convert any Google Drive URL to the reliable lh3 embed format
  const getDriveImageUrl = (event) => {
    // Use flyerDriveFileId if available (most reliable)
    if (event.flyerDriveFileId) {
      return `https://lh3.googleusercontent.com/d/${event.flyerDriveFileId}`
    }
    // Fallback: extract ID from uc?export=view URL
    if (event.flyerImageUrl) {
      const match = event.flyerImageUrl.match(/[?&]id=([^&]+)/)
      if (match) {
        return `https://lh3.googleusercontent.com/d/${match[1]}`
      }
    }
    return event.flyerImageUrl
  }

  const categories = [
    { value: 'all', label: 'All Events', color: 'gray' },
    { value: 'social', label: 'Social Events', color: 'blue' },
    { value: 'sports', label: 'Sports & Recreation', color: 'green' },
    { value: 'meeting', label: 'Meetings', color: 'purple' },
    { value: 'maintenance', label: 'Maintenance', color: 'orange' },
    { value: 'special', label: 'Special Events', color: 'pink' },
    { value: 'holiday', label: 'Holiday Events', color: 'red' }
  ]

  // Fetch events from API
  const fetchEvents = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await axios.get('/api/events')

      // Sort events by date (upcoming first)
      const sortedEvents = response.data.sort((a, b) => {
        return new Date(a.eventDate) - new Date(b.eventDate)
      })

      setEvents(sortedEvents)
      setFilteredEvents(sortedEvents)
    } catch (err) {
      console.error('Error fetching events:', err)
      setError('Failed to load events. Please try again later.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchEvents()
  }, [])

  // Filter events by category, search query, and past/future
  useEffect(() => {
    let filtered = events

    // Filter by past/future
    const today = moment().startOf('day')
    if (!showPastEvents) {
      filtered = filtered.filter(event => {
        const eventDate = moment.tz(event.eventDate, ET).startOf('day')
        return eventDate.isSameOrAfter(today)
      })
    }

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(event => event.category === selectedCategory)
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(event =>
        event.title?.toLowerCase().includes(query) ||
        event.description?.toLowerCase().includes(query) ||
        event.location?.toLowerCase().includes(query)
      )
    }

    setFilteredEvents(filtered)
  }, [selectedCategory, searchQuery, events, showPastEvents])

  // Determine if event is past, today, or upcoming
  const getEventStatus = (startDate) => {
    const today = moment().startOf('day')
    const eventDate = moment.tz(startDate, ET).startOf('day')

    if (eventDate.isBefore(today)) {
      return { status: 'past', label: 'Past Event', color: 'gray' }
    } else if (eventDate.isSame(today)) {
      return { status: 'today', label: 'Today', color: 'green' }
    } else if (eventDate.diff(today, 'days') <= 7) {
      return { status: 'soon', label: 'This Week', color: 'blue' }
    } else {
      return { status: 'upcoming', label: 'Upcoming', color: 'purple' }
    }
  }

  // Get category color
  const getCategoryColor = (category) => {
    const cat = categories.find(c => c.value === category)
    return cat ? cat.color : 'gray'
  }

  // Group events by month
  const groupEventsByMonth = (events) => {
    const grouped = {}
    events.forEach(event => {
      const monthKey = moment.tz(event.eventDate, ET).format('MMMM YYYY')
      if (!grouped[monthKey]) {
        grouped[monthKey] = []
      }
      grouped[monthKey].push(event)
    })
    return grouped
  }

  const groupedEvents = groupEventsByMonth(filteredEvents)

  // Handle opening RSVP modal
  const handleRsvpClick = (event) => {
    setSelectedEvent(event)
    setNumAdults(1)
    setNumChildren(0)
    setAttendeeName('')
    setAttendeeEmail('')
    setShowRsvpModal(true)
  }

  // Calculate total price
  const calculateTotal = () => {
    if (!selectedEvent || !selectedEvent.collectPayment) return 0
    const adultTotal = (selectedEvent.pricePerAdult || 0) * numAdults
    const childTotal = (selectedEvent.pricePerChild || 0) * numChildren
    return adultTotal + childTotal
  }

  // Handle RSVP submission (no payment)
  const handleRsvpSubmit = async () => {
    try {
      const response = await axios.post(`/api/events/${selectedEvent._id}/rsvp`, {
        attendeeName,
        attendeeEmail,
        numAdults,
        numChildren
      })

      setConfirmationNumber(response.data.confirmationNumber)
      setShowRsvpModal(false)
      setRsvpComplete(true)
    } catch (err) {
      console.error('Error submitting RSVP:', err)
      alert('Failed to submit RSVP. Please try again.')
    }
  }

  // Handle payment flow
  const handleContinueToPayment = () => {
    setShowRsvpModal(false)
    setShowPaymentForm(true)
  }

  // Handle successful payment
  const handlePaymentSuccess = async (payment) => {
    // Declared outside the try so the catch below can still report it to the
    // member -- they have already paid at this point.
    const confirmationNum = `RSVP-${Date.now()}`

    try {
      // Send confirmation email to admin
      await axios.post('/api/events/rsvp-confirmation', {
        eventId: selectedEvent._id,
        eventTitle: selectedEvent.title,
        eventDate: selectedEvent.eventDate,
        eventLocation: selectedEvent.location,
        attendeeName,
        attendeeEmail,
        numAdults,
        numChildren,
        totalAmount: payment.amounts.total,
        paymentId: payment.id,
        confirmationNumber: confirmationNum
      })

      setConfirmationNumber(confirmationNum)
      setShowPaymentForm(false)
      setRsvpComplete(true)

      console.log('Event RSVP Payment successful:', {
        eventId: selectedEvent._id,
        eventTitle: selectedEvent.title,
        numAdults,
        numChildren,
        payment,
        confirmationNumber: confirmationNum
      })
    } catch (err) {
      console.error('Error processing RSVP:', err)
      alert('Payment successful! However, there was an error sending the confirmation email. Please contact us at admin@lockridgeforestswimtennis.com with your confirmation number: ' + confirmationNum)
    }
  }

  // Handle payment cancellation
  const handlePaymentCancel = () => {
    setShowPaymentForm(false)
    setShowRsvpModal(true)
  }

  // Reset RSVP state
  const resetRsvpState = () => {
    setShowRsvpModal(false)
    setShowPaymentForm(false)
    setRsvpComplete(false)
    setSelectedEvent(null)
    setNumAdults(1)
    setNumChildren(0)
    setConfirmationNumber(null)
    setAttendeeName('')
    setAttendeeEmail('')
  }

  const handleSignupClick = async (event) => {
    try {
      setSignupEvent(event)
      setDetailEvent(null)
      const response = await axios.get(`/api/events/${event._id}/signup-slots`)
      setSignupSlots(response.data.roles)
      setSelectedRole(null)
      setSelectedSlot(null)
      setSignupName('')
      setSignupEmail('')
      setSignupDetail('')
      setSignupComplete(false)
      setShowSignupModal(true)
    } catch (err) {
      console.error('Error fetching signup slots:', err)
      alert('Failed to load signup sheet. Please try again.')
    }
  }

  const handleSignupSubmit = async () => {
    if (!canSubmitSignup) return
    try {
      setSignupSubmitting(true)
      await axios.post(`/api/events/${signupEvent._id}/signup`, {
        roleId: selectedRole,
        slotId: selectedSlot,
        attendeeName: signupName,
        attendeeEmail: signupEmail,
        detail: signupDetail,
      })
      setSignupComplete(true)
    } catch (err) {
      console.error('Error submitting signup:', err)
      alert(err.response?.data?.message || 'Failed to sign up. Please try again.')
    } finally {
      setSignupSubmitting(false)
    }
  }

  const handleSignupAnother = async () => {
    try {
      const response = await axios.get(`/api/events/${signupEvent._id}/signup-slots`)
      setSignupSlots(response.data.roles)
      setSelectedRole(null)
      setSelectedSlot(null)
      setSignupDetail('')
      setSignupComplete(false)
    } catch (err) {
      console.error('Error refreshing signup slots:', err)
    }
  }

  const formatSlotTime = (timeStr) => {
    if (!timeStr) return ''
    return moment(timeStr, 'HH:mm').format('h:mm A')
  }

  const selectedRoleObj = signupSlots?.find(r => r.roleId === selectedRole)
  const canSubmitSignup =
    !!selectedRole &&
    !!selectedSlot &&
    !!signupName.trim() &&
    !!signupEmail.trim() &&
    (!requiresDetail(selectedRoleObj) || !!signupDetail.trim())

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <section
        className="relative text-white min-h-[450px] flex items-center"
        style={{backgroundImage: 'url(/images/club/image-b032a72c.jpg)', backgroundSize: 'cover', backgroundPosition: 'center'}}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-primary-900/80 via-secondary-800/70 to-tertiary-900/80"></div>
        <div className="container-custom relative z-10 py-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="mb-6 animate-fade-in text-4xl md:text-5xl lg:text-6xl font-bold">
              Community Events
            </h1>
            <p className="text-xl md:text-2xl mb-8 leading-relaxed opacity-95">
              Stay connected with upcoming activities and gatherings at Lockridge Forest
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="container-custom py-12">
        {/* Search and Filter Section */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search Bar */}
            <div className="flex-1">
              <label htmlFor="search" className="block text-sm font-semibold text-gray-700 mb-2">
                Search Events
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by title, description, or location..."
                  className="input pr-10"
                />
                <svg
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
            </div>

            {/* Category Filter */}
            <div className="lg:w-64">
              <label htmlFor="category" className="block text-sm font-semibold text-gray-700 mb-2">
                Filter by Category
              </label>
              <select
                id="category"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="input"
              >
                {categories.map(cat => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Show Past Events Toggle */}
          <div className="mt-4 pt-4 border-t border-gray-200">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={showPastEvents}
                onChange={(e) => setShowPastEvents(e.target.checked)}
                className="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 rounded focus:ring-primary-500 focus:ring-2"
              />
              <span className="ml-2 text-sm font-medium text-gray-700">
                Show past events
              </span>
            </label>
          </div>

          {/* Active Filters Display */}
          {(selectedCategory !== 'all' || searchQuery || showPastEvents) && (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <span className="text-sm text-gray-600">Active filters:</span>
              {selectedCategory !== 'all' && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                  {categories.find(c => c.value === selectedCategory)?.label}
                  <button
                    onClick={() => setSelectedCategory('all')}
                    className="ml-2 hover:text-primary-900"
                  >
                    ×
                  </button>
                </span>
              )}
              {searchQuery && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                  Search: "{searchQuery}"
                  <button
                    onClick={() => setSearchQuery('')}
                    className="ml-2 hover:text-primary-900"
                  >
                    ×
                  </button>
                </span>
              )}
              {showPastEvents && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                  Including past events
                  <button
                    onClick={() => setShowPastEvents(false)}
                    className="ml-2 hover:text-primary-900"
                  >
                    ×
                  </button>
                </span>
              )}
              <button
                onClick={() => {
                  setSelectedCategory('all')
                  setSearchQuery('')
                  setShowPastEvents(false)
                }}
                className="text-sm text-primary-600 hover:text-primary-800 font-medium"
              >
                Clear all
              </button>
            </div>
          )}
        </div>

        {/* Events Display */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        ) : error ? (
          <div className="text-center py-20">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <p className="text-red-600 text-lg mb-4">{error}</p>
            <button onClick={fetchEvents} className="btn btn-primary">
              Try Again
            </button>
          </div>
        ) : filteredEvents.length === 0 ? (
          <div className="text-center py-20">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Events Found</h3>
            <p className="text-gray-600">
              {searchQuery || selectedCategory !== 'all'
                ? 'Try adjusting your search or filter criteria'
                : 'No events are currently scheduled. Check back soon!'}
            </p>
          </div>
        ) : (
          <div className="space-y-8">
            {Object.entries(groupedEvents).map(([month, monthEvents]) => (
              <div key={month}>
                <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                  <span className="flex-shrink-0">{month}</span>
                  <span className="ml-3 flex-1 h-px bg-gray-300"></span>
                  <span className="ml-3 text-sm font-normal text-gray-500">
                    {monthEvents.length} {monthEvents.length === 1 ? 'event' : 'events'}
                  </span>
                </h2>

                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                  {monthEvents.map(event => {
                    const eventStatus = getEventStatus(event.eventDate)
                    const categoryColor = getCategoryColor(event.category)

                    return (
                      <div
                        key={event._id}
                        className="card group hover:shadow-xl transition-all duration-300 cursor-pointer"
                        onClick={() => setDetailEvent(event)}
                      >
                        {/* Event Date Badge */}
                        <div className={`bg-${categoryColor}-600 text-white p-4 text-center`}>
                          <div className="text-3xl font-bold">
                            {moment.tz(event.eventDate, ET).format('DD')}
                          </div>
                          <div className="text-sm font-medium uppercase">
                            {moment.tz(event.eventDate, ET).format('MMM')}
                          </div>
                          {event.startTime && (
                            <div className="text-xs mt-1 opacity-90">
                              {moment(event.startTime, 'HH:mm').format('h:mm A')}
                            </div>
                          )}
                        </div>

                        {/* Event Content */}
                        <div className="p-6">
                          {/* Status and Category Badges */}
                          <div className="flex flex-wrap gap-2 mb-3">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${eventStatus.color}-100 text-${eventStatus.color}-800`}>
                              {eventStatus.label}
                            </span>
                            {event.category && (
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${categoryColor}-100 text-${categoryColor}-800`}>
                                {categories.find(c => c.value === event.category)?.label || event.category}
                              </span>
                            )}
                          </div>

                          {/* Event Title */}
                          <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors">
                            {event.title}
                          </h3>

                          {/* Event Description */}
                          <p className="text-gray-600 text-sm mb-4 line-clamp-3 whitespace-pre-line">
                            {linkifyText(event.description) || 'No description available.'}
                          </p>

                          {/* View Flyer Indicator */}
                          {event.flyerImageUrl && (
                            <div className="mb-4 flex items-center text-sm text-primary-600 font-medium">
                              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                              Click to view flyer
                            </div>
                          )}

                          {/* Event Details */}
                          <div className="space-y-2 text-sm text-gray-700">
                            {/* Time Range */}
                            {event.startTime && (
                              <div className="flex items-start font-medium">
                                <svg className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span>
                                  {moment(event.startTime, 'HH:mm').format('h:mm A')}
                                  {event.endTime && ` - ${moment(event.endTime, 'HH:mm').format('h:mm A')}`}
                                </span>
                              </div>
                            )}

                            {/* Location */}
                            {event.location && (
                              <div className="flex items-start font-medium">
                                <svg className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                <span>{event.location}</span>
                              </div>
                            )}

                            {event.endDate && moment.tz(event.endDate, ET).isAfter(moment.tz(event.eventDate), 'day') && (
                              <div className="flex items-start">
                                <svg className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span>
                                  Multi-day event through {moment.tz(event.endDate, ET).format('MMM DD, YYYY')}
                                </span>
                              </div>
                            )}

                            {event.organizer && (
                              <div className="flex items-start">
                                <svg className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                                <span>Organized by {event.organizer}</span>
                              </div>
                            )}

                            {event.requireRsvp && (
                              <div className="flex items-start text-orange-600 font-medium">
                                <svg className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                                <span>Registration Required</span>
                              </div>
                            )}
                          </div>

                          {/* RSVP/Payment Buttons */}
                          {eventStatus.status !== 'past' && (event.requireRsvp || event.collectPayment) && (
                            <div className="mt-4 pt-4 border-t">
                              <button
                                onClick={(e) => { e.stopPropagation(); handleRsvpClick(event) }}
                                className="btn btn-primary w-full text-center"
                              >
                                {event.collectPayment ? 'RSVP & View Pricing' : 'RSVP for Event'}
                              </button>
                            </div>
                          )}

                          {/* Signup Sheet Button */}
                          {eventStatus.status !== 'past' && event.hasSignupSheet && (
                            <div className={`mt-4 ${!(event.requireRsvp || event.collectPayment) ? 'pt-4 border-t' : ''}`}>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleSignupClick(event) }}
                                className="btn w-full text-center bg-orange-600 hover:bg-orange-700 text-white rounded-lg py-2 px-4 font-medium"
                              >
                                {signupCtaLabel(event.signupRoles)}
                              </button>
                            </div>
                          )}

                          {/* Contact/Registration Link - only show if no RSVP/payment */}
                          {event.contactEmail && eventStatus.status !== 'past' && !event.requireRsvp && !event.collectPayment && (
                            <div className="mt-4 pt-4 border-t" onClick={(e) => e.stopPropagation()}>
                              <a
                                href={`mailto:${event.contactEmail}`}
                                className="text-primary-600 hover:text-primary-800 text-sm font-medium flex items-center transition-colors"
                              >
                                Contact for more info
                                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                              </a>
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Info Section */}
        {!loading && !error && (
          <div className="mt-12 bg-primary-50 border-l-4 border-primary-600 p-6 rounded-lg">
            <h3 className="text-lg font-semibold text-primary-900 mb-2">
              Want to organize an event?
            </h3>
            <p className="text-primary-800 mb-4">
              If you're planning a community event and would like it featured here, please contact
              the board or submit a request through the facility reservation system.
            </p>
            <a href="/facility-reservation" className="btn btn-primary inline-block">
              Reserve a Facility
            </a>
          </div>
        )}
      </div>

      {/* Payment Form Modal */}
      {showPaymentForm && selectedEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 overflow-hidden">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full my-8 max-h-[90vh] flex flex-col overflow-hidden">
            {/* Modal Header */}
            <div className="bg-white border-b px-6 py-4 flex justify-between items-center flex-shrink-0">
              <h3 className="text-xl font-bold text-gray-900">
                Complete Payment for {selectedEvent.title}
              </h3>
              <button
                onClick={handlePaymentCancel}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-6 overflow-y-auto flex-1">
              <PaymentForm
                amount={calculateTotal()}
                description={`${selectedEvent.title} - ${numAdults} adult(s), ${numChildren} child(ren)`}
                customerEmail={attendeeEmail}
                customerName={attendeeName}
                referenceId={selectedEvent._id}
                metadata={{
                  eventId: selectedEvent._id,
                  eventTitle: selectedEvent.title,
                  numAdults,
                  numChildren,
                  eventDate: selectedEvent.eventDate,
                  attendeeName,
                  attendeeEmail
                }}
                onSuccess={handlePaymentSuccess}
                onCancel={handlePaymentCancel}
              />
            </div>
          </div>
        </div>
      )}

      {/* RSVP Confirmation Modal */}
      {rsvpComplete && selectedEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              {/* Success Icon */}
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-secondary-100 mb-4">
                <svg className="h-10 w-10 text-secondary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>

              {/* Success Message */}
              <div className="text-center">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  RSVP Confirmed!
                </h3>
                <p className="text-gray-600 mb-4">
                  {selectedEvent.collectPayment
                    ? 'Your payment has been processed successfully.'
                    : 'Your registration has been submitted successfully.'}
                </p>

                {/* Confirmation Details */}
                <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Confirmation Number:</span>
                      <span className="font-semibold text-gray-900">{confirmationNumber}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Event:</span>
                      <span className="font-semibold text-gray-900">{selectedEvent.title}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Date:</span>
                      <span className="font-semibold text-gray-900">
                        {moment.tz(selectedEvent.eventDate, ET).format('MMMM DD, YYYY')}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Attendees:</span>
                      <span className="font-semibold text-gray-900">
                        {numAdults} adult(s), {numChildren} child(ren)
                      </span>
                    </div>
                  </div>
                </div>

                <p className="text-sm text-gray-600 mb-6">
                  A confirmation email has been sent to your email address. If you don't see it in your inbox within a few minutes, please check your spam or junk folder.
                </p>

                <button
                  onClick={resetRsvpState}
                  className="btn btn-primary w-full"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* RSVP Modal */}
      {showRsvpModal && selectedEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center rounded-t-lg">
              <h3 className="text-xl font-bold text-gray-900">
                RSVP for Event
              </h3>
              <button
                onClick={() => setShowRsvpModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-4 overflow-y-auto flex-1">
              {/* Event Details */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">{selectedEvent.title}</h4>
                <div className="text-sm text-gray-600 space-y-1">
                  <div className="flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    {moment.tz(selectedEvent.eventDate, ET).format('MMMM DD, YYYY')}
                  </div>
                  {selectedEvent.startTime && (
                    <div className="flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {moment(selectedEvent.startTime, 'HH:mm').format('h:mm A')}
                    </div>
                  )}
                  {selectedEvent.location && (
                    <div className="flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      {selectedEvent.location}
                    </div>
                  )}
                </div>
              </div>

              {/* Attendee Information */}
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Your Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={attendeeName}
                    onChange={(e) => setAttendeeName(e.target.value)}
                    placeholder="Enter your full name"
                    className="input w-full"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Your Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={attendeeEmail}
                    onChange={(e) => setAttendeeEmail(e.target.value)}
                    placeholder="Enter your email address"
                    className="input w-full"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Confirmation will be sent to this email address
                  </p>
                </div>
              </div>

              {/* Attendee Count Form */}
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Number of Adults
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={numAdults}
                    onChange={(e) => setNumAdults(parseInt(e.target.value) || 0)}
                    className="input w-full"
                  />
                  {selectedEvent.collectPayment && selectedEvent.pricePerAdult > 0 && (
                    <p className="text-sm text-gray-600 mt-1">
                      ${selectedEvent.pricePerAdult.toFixed(2)} per adult
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Number of Children
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={numChildren}
                    onChange={(e) => setNumChildren(parseInt(e.target.value) || 0)}
                    className="input w-full"
                  />
                  {selectedEvent.collectPayment && selectedEvent.pricePerChild > 0 && (
                    <p className="text-sm text-gray-600 mt-1">
                      ${selectedEvent.pricePerChild.toFixed(2)} per child
                    </p>
                  )}
                </div>
              </div>

              {/* Price Summary (if payment required) */}
              {selectedEvent.collectPayment && (
                <div className="mb-6 p-4 bg-primary-50 rounded-lg border border-primary-200">
                  <div className="space-y-2 text-sm">
                    {numAdults > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-700">
                          Adults ({numAdults} × ${selectedEvent.pricePerAdult.toFixed(2)})
                        </span>
                        <span className="font-semibold text-gray-900">
                          ${(numAdults * selectedEvent.pricePerAdult).toFixed(2)}
                        </span>
                      </div>
                    )}
                    {numChildren > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-700">
                          Children ({numChildren} × ${selectedEvent.pricePerChild.toFixed(2)})
                        </span>
                        <span className="font-semibold text-gray-900">
                          ${(numChildren * selectedEvent.pricePerChild).toFixed(2)}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between pt-2 border-t border-primary-300">
                      <span className="font-bold text-gray-900">Total</span>
                      <span className="font-bold text-primary-600 text-lg">
                        ${calculateTotal().toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => setShowRsvpModal(false)}
                  className="btn btn-secondary flex-1"
                >
                  Cancel
                </button>
                {selectedEvent.collectPayment ? (
                  <button
                    onClick={handleContinueToPayment}
                    disabled={(numAdults === 0 && numChildren === 0) || !attendeeName.trim() || !attendeeEmail.trim()}
                    className="btn btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Continue to Payment
                  </button>
                ) : (
                  <button
                    onClick={handleRsvpSubmit}
                    disabled={(numAdults === 0 && numChildren === 0) || !attendeeName.trim() || !attendeeEmail.trim()}
                    className="btn btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Submit RSVP
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Event Detail Modal */}
      {detailEvent && !showRsvpModal && !showPaymentForm && !rsvpComplete && !showSignupModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full my-8">
            {/* Header with date banner */}
            <div className={`bg-${getCategoryColor(detailEvent.category)}-600 text-white p-6 rounded-t-lg`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {detailEvent.category && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-white/20 text-white">
                        {categories.find(c => c.value === detailEvent.category)?.label || detailEvent.category}
                      </span>
                    )}
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-white/20 text-white`}>
                      {getEventStatus(detailEvent.eventDate).label}
                    </span>
                  </div>
                  <h3 className="text-2xl font-bold">{detailEvent.title}</h3>
                  <p className="text-white/90 mt-1">
                    {moment.tz(detailEvent.eventDate, ET).format('dddd, MMMM DD, YYYY')}
                    {detailEvent.startTime && ` at ${moment(detailEvent.startTime, 'HH:mm').format('h:mm A')}`}
                    {detailEvent.endTime && ` - ${moment(detailEvent.endTime, 'HH:mm').format('h:mm A')}`}
                  </p>
                </div>
                <button
                  onClick={() => setDetailEvent(null)}
                  className="text-white/80 hover:text-white transition-colors flex-shrink-0 ml-4"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-6 space-y-5">
              {/* Flyer Image */}
              {detailEvent.flyerImageUrl && (
                <div>
                  <img
                    src={getDriveImageUrl(detailEvent)}
                    alt={`${detailEvent.title} flyer`}
                    className="w-full rounded-lg shadow-md cursor-pointer hover:opacity-95 transition-opacity"
                    onClick={() => setLightboxImage({ url: getDriveImageUrl(detailEvent), title: detailEvent.title })}
                  />
                  <p className="text-xs text-gray-500 mt-1 text-center">Click image to enlarge</p>
                </div>
              )}

              {/* Description */}
              {detailEvent.description && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Description</h4>
                  <p className="text-gray-700 whitespace-pre-line leading-relaxed">{linkifyText(detailEvent.description)}</p>
                </div>
              )}

              {/* Event Details */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {detailEvent.location && (
                  <div className="flex items-start">
                    <svg className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase">Location</p>
                      <p className="text-gray-900">{detailEvent.location}</p>
                    </div>
                  </div>
                )}
                {detailEvent.organizer && (
                  <div className="flex items-start">
                    <svg className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase">Organizer</p>
                      <p className="text-gray-900">{detailEvent.organizer}</p>
                    </div>
                  </div>
                )}
                {detailEvent.endDate && moment.tz(detailEvent.endDate, ET).isAfter(moment.tz(detailEvent.eventDate), 'day') && (
                  <div className="flex items-start">
                    <svg className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase">Ends</p>
                      <p className="text-gray-900">{moment.tz(detailEvent.endDate, ET).format('MMMM DD, YYYY')}</p>
                    </div>
                  </div>
                )}
                {detailEvent.contactEmail && (
                  <div className="flex items-start">
                    <svg className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase">Contact</p>
                      <a href={`mailto:${detailEvent.contactEmail}`} className="text-primary-600 hover:text-primary-800">{detailEvent.contactEmail}</a>
                    </div>
                  </div>
                )}
              </div>

              {/* Pricing info */}
              {detailEvent.collectPayment && (
                <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-primary-900 mb-2">Pricing</h4>
                  <div className="text-sm text-primary-800 space-y-1">
                    {detailEvent.pricePerAdult > 0 && <p>${detailEvent.pricePerAdult.toFixed(2)} per adult</p>}
                    {detailEvent.pricePerChild > 0 && <p>${detailEvent.pricePerChild.toFixed(2)} per child</p>}
                  </div>
                </div>
              )}

              {/* RSVP Button */}
              {getEventStatus(detailEvent.eventDate).status !== 'past' && (detailEvent.requireRsvp || detailEvent.collectPayment) && (
                <button
                  onClick={(e) => { e.stopPropagation(); handleRsvpClick(detailEvent); setDetailEvent(null) }}
                  className="btn btn-primary w-full text-center"
                >
                  {detailEvent.collectPayment ? 'RSVP & Pay' : 'RSVP for Event'}
                </button>
              )}

              {/* Signup Sheet Button */}
              {getEventStatus(detailEvent.eventDate).status !== 'past' && detailEvent.hasSignupSheet && (
                <button
                  onClick={(e) => { e.stopPropagation(); handleSignupClick(detailEvent) }}
                  className="btn w-full text-center bg-orange-600 hover:bg-orange-700 text-white rounded-lg py-2 px-4 font-medium"
                >
                  {signupCtaLabel(detailEvent.signupRoles)}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Signup Modal */}
      {showSignupModal && signupEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center rounded-t-lg">
              <h3 className="text-xl font-bold text-gray-900">
                {signupComplete ? 'Signed Up!' : signupCtaLabel(signupSlots)}
              </h3>
              <button
                onClick={() => setShowSignupModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="px-6 py-4 overflow-y-auto flex-1">
              {signupComplete ? (
                <div className="text-center py-6">
                  <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
                    <svg className="h-10 w-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">You're signed up!</h4>
                  <p className="text-gray-600 mb-6">Thank you! We have you down.</p>
                  <div className="flex gap-3">
                    <button onClick={handleSignupAnother} className="btn btn-secondary flex-1">
                      Sign Up for Something Else
                    </button>
                    <button onClick={() => setShowSignupModal(false)} className="btn btn-primary flex-1">
                      Done
                    </button>
                  </div>
                </div>
              ) : signupSlots ? (
                <>
                  <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-2">{signupEvent.title}</h4>
                    <p className="text-sm text-gray-600">
                      {moment.tz(signupEvent.eventDate, ET).format('MMMM DD, YYYY')}
                    </p>
                  </div>

                  <div className="space-y-4 mb-6">
                    {signupSlots.map(role => (
                      <div key={role.roleId}>
                        <h5 className="text-sm font-semibold text-gray-800">{role.roleName}</h5>
                        <p className="text-xs text-gray-500 mb-2">
                          {role.description || getSignupType(role).selectPrompt}
                        </p>
                        <div className="space-y-2 ml-2">
                          {role.slots.map(slot => {
                            const isSelected = selectedRole === role.roleId && selectedSlot === slot.slotId
                            return (
                              <button
                                key={slot.slotId}
                                type="button"
                                disabled={slot.isFull}
                                onClick={() => {
                                  setSelectedRole(role.roleId)
                                  setSelectedSlot(slot.slotId)
                                  setSignupDetail('')
                                }}
                                className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-colors ${
                                  slot.isFull
                                    ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                                    : isSelected
                                    ? 'border-orange-500 bg-orange-50 text-orange-800'
                                    : 'border-gray-200 hover:border-orange-300 hover:bg-orange-50'
                                }`}
                              >
                                <div className="flex justify-between items-center">
                                  <span className="font-medium text-sm">
                                    {formatSlotLabel(role, slot, formatSlotTime)}
                                  </span>
                                  <span className={`text-xs font-medium ${slot.isFull ? 'text-red-500' : 'text-green-600'}`}>
                                    {slot.isFull ? 'Full' : `${slot.capacity - slot.filledCount} spot${slot.capacity - slot.filledCount !== 1 ? 's' : ''} left`}
                                  </span>
                                </div>
                              </button>
                            )
                          })}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="space-y-4 mb-6">
                    {requiresDetail(selectedRoleObj) && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          {getSignupType(selectedRoleObj).detailLabel} <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={signupDetail}
                          onChange={(e) => setSignupDetail(e.target.value)}
                          placeholder={getSignupType(selectedRoleObj).detailPlaceholder}
                          className="input w-full"
                        />
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Your Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={signupName}
                        onChange={(e) => setSignupName(e.target.value)}
                        placeholder="Enter your full name"
                        className="input w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Your Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        value={signupEmail}
                        onChange={(e) => setSignupEmail(e.target.value)}
                        placeholder="Enter your email address"
                        className="input w-full"
                      />
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <button onClick={() => setShowSignupModal(false)} className="btn btn-secondary flex-1">
                      Cancel
                    </button>
                    <button
                      onClick={handleSignupSubmit}
                      disabled={!canSubmitSignup || signupSubmitting}
                      className="btn btn-primary flex-1 bg-orange-600 hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {signupSubmitting ? 'Signing up...' : 'Sign Up'}
                    </button>
                  </div>
                </>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {/* Flyer Lightbox */}
      {lightboxImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4 cursor-pointer"
          onClick={() => setLightboxImage(null)}
        >
          <div className="relative max-w-4xl w-full max-h-[90vh] flex items-center justify-center">
            <button
              onClick={() => setLightboxImage(null)}
              className="absolute -top-10 right-0 text-white hover:text-gray-300 transition-colors"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <img
              src={lightboxImage.url}
              alt={`${lightboxImage.title} flyer`}
              className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default Events
